import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Sequence } from '@/types/sequence';

interface HolidayDiscoveryData {
  trending: Sequence[];
  latest: Sequence[];
  isLoading: boolean;
  error: string | null;
}

interface RawSequence {
  id: string;
  name: string;
  title: string | null;
  description: string | null;
  image_url: string | null;
  album_cover_url: string | null;
  video_url: string | null;
  price: number | null;
  is_free: boolean | null;
  download_count: number | null;
  upvote_count: number;
  software_type: string | null;
  holiday_type: string | null;
  genres: string[];
  created_at: string | null;
  user_id: string | null;
  artist_id: string | null;
  artists?: {
    name: string;
  } | null;
}

/**
 * Custom hook to load trending and latest sequences for a specific holiday
 * Used by HolidayDiscoverySection to populate discovery carousels
 */
export function useHolidayDiscoveryData(holiday: string | null): HolidayDiscoveryData {
  const [trending, setTrending] = useState<Sequence[]>([]);
  const [latest, setLatest] = useState<Sequence[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!holiday) {
      setIsLoading(false);
      return;
    }

    const loadDiscoveryData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Load trending sequences (by download count)
        // Join with artists table for artist name
        // Note: Cannot join auth.users via PostgREST due to cross-schema limitations
        const { data: trendingData, error: trendingError } = await supabase
          .from('sequences')
          .select('*, artists(name)')
          .eq('holiday_type', holiday)
          .order('download_count', { ascending: false })
          .limit(8);

        if (trendingError) {
          console.error('Trending sequences query error:', trendingError);
          console.error('Holiday:', holiday);
        }

        // Load latest sequences (by created date)
        // Join with artists table for artist name
        // Note: Cannot join auth.users via PostgREST due to cross-schema limitations
        const { data: latestData, error: latestError } = await supabase
          .from('sequences')
          .select('*, artists(name)')
          .eq('holiday_type', holiday)
          .order('created_at', { ascending: false })
          .limit(8);

        if (latestError) {
          console.error('Latest sequences query error:', latestError);
          console.error('Holiday:', holiday);
        }

        // Transform to Sequence type with defensive null checks
        const transformSequence = (raw: any): Sequence | null => {
          try {
            // Validate required fields
            if (!raw || !raw.id) {
              console.warn('Invalid sequence data - missing ID:', raw);
              return null;
            }

            return {
              id: raw.id,
              displayName: raw.name || 'Untitled',
              title: raw.title || raw.name || 'Untitled',
              description: raw.description || '',
              imageUrl: raw.image_url || raw.album_cover_url || '',
              videoUrl: raw.video_url || '',
              price: raw.price ?? 0,
              isFree: raw.is_free ?? false,
              downloadCount: raw.download_count ?? 0,
              upvoteCount: raw.upvote_count ?? 0,
              averageRating: 4.0, // Default rating, would need to be calculated from ratings table
              software: raw.software_type || 'Unknown',
              holiday: raw.holiday_type || 'Other',
              genres: Array.isArray(raw.genres) ? raw.genres : [],
              createdAt: raw.created_at || new Date().toISOString(),
              // Use joined artist data; uploader name not available due to PostgREST cross-schema limitations
              uploaderName: 'Community Member',
              artistName: raw.artists?.name || 'Unknown Artist',
              artistId: raw.artist_id || '',
            };
          } catch (transformErr) {
            console.error('Error transforming sequence:', transformErr, raw);
            return null;
          }
        };

        // Transform and filter out null results
        const transformedTrending = (trendingData || [])
          .map(transformSequence)
          .filter((seq): seq is Sequence => seq !== null);

        const transformedLatest = (latestData || [])
          .map(transformSequence)
          .filter((seq): seq is Sequence => seq !== null);

        setTrending(transformedTrending);
        setLatest(transformedLatest);

        // Log success
        console.log(`Loaded ${transformedTrending.length} trending and ${transformedLatest.length} latest sequences for ${holiday}`);
      } catch (err) {
        console.error('Error loading holiday discovery data:', err);
        console.error('Holiday:', holiday);

        // Set error but don't break the page - return empty arrays
        setTrending([]);
        setLatest([]);
        setError(err instanceof Error ? err.message : 'Failed to load discovery data');
      } finally {
        setIsLoading(false);
      }
    };

    loadDiscoveryData();
  }, [holiday]);

  return {
    trending,
    latest,
    isLoading,
    error,
  };
}
